import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { PageType } from '@zotlo-shared/types/model';
import { useSiteDataOnClient } from '~/composables/useSiteDataOnClient';
import { prepareFontUrl } from '@zotlo-shared/utils';
import { View } from '@/services/view';
import { Integration } from "@/utils/integrations";
import { ProviderSdks } from "#shared/utils/providers";
import type { PageOptions, Page } from '@zotlo-shared/types/model';
import { getClientCookie, COOKIE } from '#shared/utils/cookie';
import { getUserAgent } from '@/utils';
import { useLanding } from './useLanding';

export function usePage(slug: PageType) {
  const { t: $t, setLocale } = useI18n();
  const { currentPage, isPreview, isStoreFlow, siteData, language, config, applicationLinks, isMaintenance, parametersNotProvided } = useSiteDataOnClient();
  const isUserAlreadySubscribed = !!getClientCookie(COOKIE.USER_ALREADY_SUBSCRIBED);
  const countdownStartedAtCookie = getClientCookie(COOKIE.COUNTDOWN_STARTED_AT);
  const page = ref<Page>(JSON.parse(currentPage.value?.page || '{}'));
  const pageOptions = ref<PageOptions>(JSON.parse(siteData.value?.pageOptions || '{}'));
  const { query, full: fontUrl } = prepareFontUrl([pageOptions.value.fontFamily, ...(pageOptions.value.fontFamilies?.[slug] || [])]);
  const headLinks = [] as any[];

  // Remove zotlo footer if wants to hide
  if (siteData.value?.hideGenerateFooter) {
    const zotloFooter = page.value.sections.find((section) => section.type === 'zotloFooter');
    if (zotloFooter) page.value.sections.splice(page.value.sections.indexOf(zotloFooter), 1);
  }

  if (import.meta.server || import.meta.browser || import.meta.client) {
    // Set valid locale for SSR CSR
    if (siteData.value?.lang) setLocale(language.value);
  }

  if (config.value.favicon && !isStoreFlow.value) {
    headLinks.push({
      rel: 'icon',
      type: 'image/x-icon',
      href: config.value.favicon
    });
  }

  if (query) {
    headLinks.push(...[
      { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
      { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossorigin: true },
      { rel: 'stylesheet', href: fontUrl }
    ]);
  }

  if (isStoreFlow.value) {
    // If store flow, add zotlo store's primary font family
    headLinks.push({
      rel: 'stylesheet', href: prepareFontUrl(['Red Hat Display'])?.full
    })
    // If store flow, favicon must be store's favicon.
    const storesFavIconUrl = `${useRuntimeConfig()?.NUXT_ZOTLO_STORE_URL as string}favicon.ico`
    headLinks.push({
      rel: 'icon',
      type: 'image/x-icon',
      href: storesFavIconUrl
    });
  }

  if (!import.meta.server && !isMaintenance.value && !parametersNotProvided.value &&  !isPreview.value && !isUserAlreadySubscribed) {
    View.page(getUserAgent(page.value?.type as PageType));
  }

  if (!import.meta.server) {
    const uaInfo = getUserAgent();
    if (uaInfo.platform !== 'desktop') {
      document?.body?.classList?.add("is-mobile-like-device");
    }
  }

  let pageProps;

  if (!isMaintenance.value && !parametersNotProvided.value) {
    if (slug === PageType.LANDING) {
      const { pageData } = useLanding(page);
      setVisitedPages(slug);
      pageProps = pageData;
    }
  
    if (slug === PageType.QUIZ) {
      const { pageData } = useSurvey(page);
      pageProps = pageData;
    }
  
    if (slug === PageType.REGISTER) {
      const { pageData } = useRegister(page);
      pageProps = pageData;
    }
  
    if (slug === PageType.PAYWALL) {
      const { pageData } = usePaywall(page);
      pageProps = pageData;
    }
  
    if (slug === PageType.PAYMENT) {
      const { pageData } = usePayment(page);
      pageProps = pageData;
    }
  
    if (slug === PageType.PAYMENT_SUCCESS) {
      const { pageData } = usePaymentSuccess(page);
      pageProps = pageData;
    }
  }

  if (pageProps?.value) {
    pageProps.value.applicationLinks = applicationLinks;
    if (countdownStartedAtCookie) {
      const elapsedCountdownTime = Math.floor((Date.now() - parseInt(countdownStartedAtCookie)) / 1000);
      pageProps.value.elapsedCountdownTime = elapsedCountdownTime;
    }
  }

  const integrationsInit = Integration.init({
    integrations: siteData.value?.integrations || {},
    preview: !!siteData.value?.preview,
    pageSlug: siteData.value?.pageSlug || '',
    countryCode: siteData.value?.countryCode || '',
    registerType: siteData.value?.registerType,
    subscriberId: siteData.value?.subscriberId,
    ipAddress: siteData.value?.ipAddress,
  });
  const providerSdksInit = ProviderSdks.init({
    providers: siteData.value?.providers || {} as any,
    preview: !!siteData.value?.preview,
    page: siteData.value?.page || {}
  });
  const headScripts = [ ...integrationsInit.script, ...providerSdksInit.script ];
  const headNoscripts = [ ...integrationsInit.noscript, ...providerSdksInit.noscript ];
  const pageSettings = pageOptions.value?.pages?.[slug] || {};
  const forceDesktopBG = !!pageSettings.forceDesktopBG;
  const { desktop, tablet, mobile } = pageSettings.backgroundImage || {};
  const bg = {
    color: pageSettings.backgroundColor,
    image: {
      desktop: desktop || '',
      tablet: (forceDesktopBG ? desktop : tablet) || '',
      mobile: (forceDesktopBG ? desktop : mobile) || '',
    },
    repeat: pageSettings.backgroundRepeat ? 'repeat' : 'no-repeat',
    size: pageSettings.backgroundRepeat ? 'auto' : 'cover',
  };
  const isWebsiteRedirectionActive = slug === PageType.PAYMENT_SUCCESS && !!siteData.value?.websiteRedirectionSettings?.status;

  let title = '';

  if (config.value.appName) {
    title = `${config.value.appName} | ${$t(`generate.page.${currentPage.value?.template?.name}`)}`;
  } else if (isMaintenance.value) {
    title = $t('generate.maintenance.desc');
  } else if (parametersNotProvided.value) {
    title = $t('generate.noparams.title');
  }

  return {
    page,
    pageData: pageProps,
    pageOptions,
    isMaintenance,
    parametersNotProvided,
    head: {
      title,
      htmlAttrs: {
        dir: siteData.value?.languageData?.isRightAlignment ? 'rtl' : 'ltr',
        lang: siteData.value?.lang,
      },
      link: headLinks,
      script: headScripts,
      noscript: headNoscripts,
      style: [
        `:root {
          --app-backgroundColor: ${bg.color};
          --app-backgroundImage-desktop: ${bg.image.desktop};
          --app-backgroundImage-tablet: ${bg.image.tablet};
          --app-backgroundImage-mobile: ${bg.image.mobile};
          --app-backgroundRepeat: ${bg.repeat};
          --app-backgroundSize: ${bg.size};
        }`
      ]
    } as Parameters<typeof useHead>[0],
    isWebsiteRedirectionActive,
    countdownStartedAtCookie,
  }
}
